export const OPM_CALCULATOR_SPREADSHEET_TITLE = 'Option Pricing Model';

// Placeholder shown until the analyst has filled in every input. The result tables cannot be
// computed from a partial set, so the panel renders only the inputs sheet up to that point.
export const OPM_CALCULATOR_PLACEHOLDER_TAGLINE =
  'Select a cap table and enter an equity value, maturity and volatility to calculate the option ladder.';

/** Caption above the inputs of a calculator a "Show OPM Calculations" link just filled in. */
export const getOPMCalculatorPrefilledFromCaption = (sourceLabel: string) => `Prefilled from ${sourceLabel}`;

export const OPM_CALCULATOR_PREFILLED_FROM_TEST_ID = 'opm-calculator-prefilled-from';

export const OPM_CALCULATOR_ERROR_TITLE = 'The Option Pricing Model could not be calculated';

// -- Sheet names -------------------------------------------------------------------------
/** Base logical names; each instance gets a unique workbook key per sheet. */
export const OPM_CALCULATOR_INPUTS_SHEET_NAME = 'opmCalculatorInputs';
export const OPM_CALCULATOR_LADDER_SHEET_NAME = 'opmCalculatorLadder';
export const OPM_CALCULATOR_ALLOCATION_SHEET_NAME = 'opmCalculatorAllocation';
export const OPM_CALCULATOR_SECURITY_SHEET_NAME = 'opmCalculatorSecurity';

/**
 * Unique `SpreadsheetConfig.name` per panel per sheet.
 *
 * Underscores only: these keys are usable as cross-sheet expression identifiers, and a panel
 * id is a uuid full of hyphens. Mirrors `getRangeSensitivityWorkbookKey`.
 */
const workbookKey = (base: string, panelId: string): string =>
  `${base}_${String(panelId ?? '').replaceAll('-', '_')}`;

export const getOPMCalculatorInputsWorkbookKey = (panelId: string): string =>
  workbookKey(OPM_CALCULATOR_INPUTS_SHEET_NAME, panelId);
export const getOPMCalculatorLadderWorkbookKey = (panelId: string): string =>
  workbookKey(OPM_CALCULATOR_LADDER_SHEET_NAME, panelId);
export const getOPMCalculatorAllocationWorkbookKey = (panelId: string): string =>
  workbookKey(OPM_CALCULATOR_ALLOCATION_SHEET_NAME, panelId);
export const getOPMCalculatorSecurityWorkbookKey = (panelId: string): string =>
  workbookKey(OPM_CALCULATOR_SECURITY_SHEET_NAME, panelId);

/**
 * Header-row styling, matching the other valuation sheets: the row title renders bold in the
 * titles column and the content cells render as column headers.
 */
export const HEADER_ROW_CLASS_NAME = 'bold large-header';

// -- Inputs sheet ------------------------------------------------------------------------
export const OPM_CALCULATOR_INPUTS_COLUMN_HEADER = 'Inputs';

export const CAP_TABLE_SELECTION_TITLE = 'Cap Table Selection';
export const CAP_TABLE_SELECTION_ALIAS = 'cap_table';
export const EQUITY_VALUE_TITLE = 'Equity Value';
export const EQUITY_VALUE_ALIAS = 'equity_value';
export const RISK_FREE_RATE_TITLE = 'Risk-free Rate';
export const RISK_FREE_RATE_ALIAS = 'risk_free_rate';
export const MATURITY_TITLE = 'Maturity';
export const MATURITY_ALIAS = 'maturity';
export const VOLATILITY_TITLE = 'Volatility';
export const VOLATILITY_ALIAS = 'volatility';
/**
 * The date the ladder is priced at. The last row, not next to maturity, so the existing rows keep
 * their cell keys.
 */
export const OPM_DATE_TITLE = 'OPM Date';
export const OPM_DATE_ALIAS = 'opm_date';

/** Same ceiling the Equity Allocation maturity cell enforces. */
export const OPM_CALCULATOR_MATURITY_MAX_VALUE = 999.99;

/**
 * `OPMCalculator.maturity` is `DecimalField(max_digits=5, decimal_places=2)` and the results
 * request serializer mirrors that precision, so a third decimal is a 400 rather than a rounded
 * value. Display, editing, and the committed number all use this so the cell cannot invite
 * precision the column will not take.
 */
export const OPM_CALCULATOR_MATURITY_DECIMAL_PLACES = 2;

// -- Ladder sheet ------------------------------------------------------------------------
export const BREAKPOINTS_TITLE = 'Breakpoints';
export const BLACK_SCHOLES_SECTION_TITLE = 'Black-Scholes Inputs and Calculations';
export const CURRENT_EQUITY_VALUE_TITLE = 'Current Equity Value (Price)';
export const EXERCISE_PRICE_TITLE = 'Exercise Price';
export const D1_TITLE = 'd1';
export const D2_TITLE = 'd2';
export const CALL_OPTION_VALUE_TITLE = 'Call Option Value:';
export const SIZE_OF_INCREMENT_TITLE = 'Size of Increment';
/** Labels as in the Excel export's `OPM` sheet, which this page mirrors. */
export const VALUE_OF_INCREMENT_TITLE = 'Value of Increment:';
export const TOTAL_ROW_TITLE = 'Total';

/** Column header for breakpoint `n` (1-indexed), matching the design. */
export const breakpointColumnHeader = (index: number): string => `Breakpoint ${index + 1}`;

/** Black-Scholes has no d1/d2 at a zero or infinite strike; the backend sends this through. */
export const NOT_APPLICABLE = 'N/A';

// -- Allocation sheet --------------------------------------------------------------------
export const ALLOCATION_OF_INCREMENTAL_OPTION_VALUE_TITLE = 'Allocation of Incremental Option Value…';

// -- Security sheet ----------------------------------------------------------------------
export const SECURITY_TITLE = 'Security';
export const TOTAL_ALLOCATION_TITLE = 'Total Allocation';
export const SHARES_OUTSTANDING_TITLE = 'Shares Outstanding';
export const VALUE_PER_SHARE_TITLE = 'Value per Share';

export const OPM_CALCULATOR_SECURITY_COLUMN_HEADERS = [
  TOTAL_ALLOCATION_TITLE,
  SHARES_OUTSTANDING_TITLE,
  VALUE_PER_SHARE_TITLE,
];
